/**
 * The team -> division index.
 *
 * `useUserRegistrationTeams` cannot help here: it is fetched FROM a division id
 * plus a role, so it cannot run before a division exists. The registration
 * payload's top-level `teams[]` can: each team carries the division it belongs
 * to, and a team belongs to exactly one, so a team resolves a division with no
 * extra request.
 */

import { divisionSelectableForRole } from './divisionRole'
import { teamIdentity, teamMatchesId } from './teamDisplay'
import type { DivisionTeam, StartDivision, StartRole, StartTeam } from './types'

/** A team from the top-level `teams[]`, carrying the division it belongs to. */
interface TeamWithDivision<T extends StartDivision> extends StartTeam {
  division: T
}

/**
 * Build the index from the top-level `teams[]`.
 *
 * Each team already carries its division, so there is nothing to flatten.
 */
export const teamsFromRegistration = <T extends StartDivision>(
  teams: TeamWithDivision<T>[] | null | undefined,
  role?: StartRole | null
): DivisionTeam<T>[] => {
  const index: DivisionTeam<T>[] = []

  for (const entry of teams ?? []) {
    if (!teamIdentity(entry) || !entry.division) {
      continue
    }
    // A team in a division that cannot accept this reader is a dead end.
    if (role && !divisionSelectableForRole(entry.division, role)) {
      continue
    }
    const { division, ...team } = entry
    index.push({ team, division })
  }

  return index
}

/**
 * Find one team by id, with its division.
 *
 * Used to resolve `?team_id=` into both a team and the division that owns it.
 * Unfiltered by role on purpose: the caller needs to tell "no such team" apart
 * from "that team is not open to this role", and can only do that if the lookup
 * itself does not hide the second case.
 *
 * Matches `uid` or `id` via `teamMatchesId`, the same pair the register step's
 * resolver accepts — the two run against different lists but read the same
 * link, so a team findable by one must be findable by the other.
 */
export const findDivisionTeamById = <T extends StartDivision>(
  teams: TeamWithDivision<T>[] | null | undefined,
  teamId: string | null | undefined
): DivisionTeam<T> | null => {
  if (!teamId) {
    return null
  }
  for (const entry of teams ?? []) {
    if (entry.division && teamMatchesId(entry, teamId)) {
      const { division, ...team } = entry
      return { team, division }
    }
  }
  return null
}
